import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { createLogger } from "../utils/logger";
import type { User } from "../types";

// Re-export User type for consumers
export type { User };

const log = createLogger({ module: "auth" });

// App type for canEditApp helper
interface AppForEdit {
  createdBy?: string | null;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isDeveloper: boolean;
  canEditApp: (app: AppForEdit) => boolean;
  login: () => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/me", {
        credentials: "include",
      });

      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
        log.debug("User authenticated", { userId: userData.id, email: userData.email });
      } else if (res.status === 401) {
        setUser(null);
        log.debug("User not authenticated");
      } else {
        log.warn("Failed to fetch user", { status: res.status });
        setUser(null);
      }
    } catch (error) {
      log.error("Error fetching user", { error });
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = useCallback(() => {
    // Redirect to backend login endpoint which handles Google OAuth
    // In development, we need to redirect to the backend directly since
    // window.location.href doesn't go through Vite's proxy
    const apiBase = import.meta.env.DEV ? "http://localhost:8080" : "";
    window.location.href = `${apiBase}/api/auth/login`;
  }, []);

  const logout = useCallback(async () => {
    try {
      // Get CSRF token first
      const csrfRes = await fetch("/api/csrf-token", { credentials: "include" });
      const { csrfToken } = await csrfRes.json();

      const res = await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
        headers: {
          "x-csrf-token": csrfToken,
        },
      });

      if (res.ok) {
        setUser(null);
        log.info("User logged out");
        // Redirect to login page
        window.location.href = "/login";
      } else {
        log.warn("Logout failed", { status: res.status });
      }
    } catch (error) {
      log.error("Error during logout", { error });
    }
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  const canEditApp = useCallback(
    (app: AppForEdit) => {
      if (!user) return false;
      if (user.role === "admin") return true;
      if (user.role === "developer") return app.createdBy === user.email;
      return false;
    },
    [user]
  );

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
    isDeveloper: user?.role === "developer",
    canEditApp,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
